import React from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  Divider,
  Stack,
  Chip
} from '@mui/material';
import TimerIcon from '@mui/icons-material/Timer';
import AssignmentIcon from '@mui/icons-material/Assignment';
import StarIcon from '@mui/icons-material/Star';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import WarningIcon from '@mui/icons-material/Warning';
import WifiIcon from '@mui/icons-material/Wifi';
import VideocamIcon from '@mui/icons-material/Videocam';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getSections, getSecondsPerQuestion, sectionIcon } from '../testSections';

export default function TestGuidelines({ test, onStartTest }) {
  const sections = getSections(test);
  const secondsPerQuestion = getSecondsPerQuestion(test);
  const totalQuestions = sections.reduce((sum, section) => sum + section.questions.length, 0);
  const totalMinutes = Math.round((secondsPerQuestion * totalQuestions) / 60);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: 4
      }}
    >
      <Box 
        component="img"
        src="/marmin_logo.png"
        alt="Marmin Logo"
        sx={{ 
          height: 80,
          mb: 4
        }}
      />
      
      <Paper elevation={3} sx={{ p: 4, width: '100%', maxWidth: 600, borderRadius: 2 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
          Test Guidelines
        </Typography>
        
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
            {sections.map((section) => (
              <Chip
                key={section.key}
                icon={sectionIcon(section.key)}
                label={`${section.label}: ${section.questions.length} questions`}
                color={section.key === 'programming' ? 'secondary' : 'primary'}
                variant="outlined"
                sx={{ fontSize: 16, px: 2 }}
              />
            ))}
          </Stack>
        </Box>

        <Divider sx={{ my: 3 }} />

        <List>
          <ListItem>
            <ListItemIcon>
              <AssignmentIcon color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="Total Questions" 
              secondary={`${totalQuestions} questions to be answered`}
              primaryTypographyProps={{ fontWeight: 500 }}
            />
          </ListItem>
          
          <ListItem>
            <ListItemIcon>
              <TimerIcon color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="Time Limit" 
              secondary={`${secondsPerQuestion} seconds per question (${totalMinutes} minutes total)`}
              primaryTypographyProps={{ fontWeight: 500 }}
            />
          </ListItem>
          
          <ListItem>
            <ListItemIcon>
              <StarIcon color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="Scoring" 
              secondary={`${test.pointsPerQuestion || 1} mark for correct answer, -0.5 marks for wrong answer`}
              primaryTypographyProps={{ fontWeight: 500 }}
            />
          </ListItem>
        </List>

        <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.200', borderRadius: 1 }}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <WarningIcon color="warning" />
            <Typography variant="subtitle2" fontWeight={600}>
              Important Notes
            </Typography>
          </Box>
          <List dense>
            <ListItem>
              <ListItemIcon>
                <WifiIcon color="warning" />
              </ListItemIcon>
              <ListItemText 
                primary="A stable internet connection is essential for a smooth test experience"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <VideocamIcon color="warning" />
              </ListItemIcon>
              <ListItemText 
                primary=
                    "Please ensure your camera remains active throughout the test. Camera deactivation will result in test disqualification."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <RefreshIcon color="warning" />
              </ListItemIcon>
              <ListItemText 
                primary="Refreshing or closing the browser will automatically skip the current question and cannot be undone"
              />
            </ListItem>
          </List>
        </Box>

        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<PlayArrowIcon />}
            onClick={onStartTest}
            sx={{ 
              minWidth: 200,
              py: 1.5,
              fontSize: '1.1rem',
              textTransform: 'none',
              boxShadow: 2,
              '&:hover': {
                boxShadow: 4
              }
            }}
          >
            Start Test
          </Button>
        </Box>
      </Paper>
    </Box>
  );
} 